import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCleaners, updateCleaner, deleteCleaner, getAssignments } from '../services/api';
import LoadingScreen from '../components/LoadingScreen';

// ── Shared inline styles (matching PropertySettingsTab) ─────────────
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--border-input)',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
  backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
};
const selectStyle = { ...inputStyle };
const sectionLabel = {
  fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.5px',
};
const sectionContainer = {
  marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-light)',
};
const fieldGroup = { display: 'flex', flexDirection: 'column', gap: '4px' };

// ─────────────────────────────────────────────────────────────────────
const CleanerManagePage = () => {
  const { cleanerId: rawCleanerId } = useParams();
  const cleanerId = parseInt(rawCleanerId);
  const navigate = useNavigate();

  // Core data
  const [cleaner, setCleaner] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Property assignments for this cleaner
  const [assignments, setAssignments] = useState([]);

  // ── Fetch helpers ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cleanersData, assignmentsResponse] = await Promise.all([
        getCleaners(),
        getAssignments(),
      ]);

      // Find this cleaner
      const found = (cleanersData || []).find(c => c.id === cleanerId);
      if (found) {
        setCleaner(found);
        setEditedData({
          name: found.name || '',
          phone: found.phone || '',
          preferred_language: found.preferred_language || 'es',
        });
      }

      // Filter assignments for this cleaner
      const allAssignments = Array.isArray(assignmentsResponse.data)
        ? assignmentsResponse.data
        : (Array.isArray(assignmentsResponse) ? assignmentsResponse : []);
      setAssignments(
        allAssignments
          .filter(a => a.cleaner_id === cleanerId)
          .sort((a, b) => a.priority - b.priority)
      );
    } catch (err) {
      console.error('Error fetching cleaner data:', err);
    }
    setLoading(false);
  }, [cleanerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Field change helper ───────────────────────────────────────────
  const handleChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  // ── Detect unsaved changes ────────────────────────────────────────
  const hasChanges = useCallback(() => {
    if (!cleaner) return false;
    return (
      editedData.name !== (cleaner.name || '') ||
      editedData.phone !== (cleaner.phone || '') ||
      editedData.preferred_language !== (cleaner.preferred_language || 'es')
    );
  }, [cleaner, editedData]);

  // ── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCleaner(cleanerId, {
        name: editedData.name,
        phone: editedData.phone,
        preferred_language: editedData.preferred_language,
      });
      // Refresh cleaner data from server
      const cleanersData = await getCleaners();
      const updated = (cleanersData || []).find(c => c.id === cleanerId);
      if (updated) {
        setCleaner(updated);
        setEditedData({
          name: updated.name || '',
          phone: updated.phone || '',
          preferred_language: updated.preferred_language || 'es',
        });
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      console.error('Error saving cleaner:', err);
      alert('Failed to save aynista changes.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this aynista? This action cannot be undone.'
    );
    if (!confirmed) return;
    try {
      await deleteCleaner(cleanerId);
      navigate('/cleaners');
    } catch (err) {
      console.error('Error deleting cleaner:', err);
      alert('Failed to delete aynista.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  if (loading) return <LoadingScreen />;

  if (!cleaner) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Aynista not found.
      </div>
    );
  }

  return (
    <div className="ayni-page" style={{ minHeight: 'calc(100vh - 40px)' }}>
      {/* Header */}
      <button className="ayni-back" onClick={() => navigate('/cleaners')}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        Back to aynistas
      </button>
      <div className="ayni-head">
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>{editedData.name || 'Untitled Aynista'}</h1>
      </div>

      {/* Settings Form */}
      <div style={{ padding: '24px', maxWidth: '600px' }}>

        {/* Form Header with Save */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="ayni-display-md" style={{ margin: 0 }}>Details</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {savedFlash && (
              <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 500 }}>Saved!</span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges()}
              style={{
                padding: '8px 18px', borderRadius: '6px', border: 'none', fontSize: '13px',
                fontWeight: '500', cursor: saving || !hasChanges() ? 'not-allowed' : 'pointer',
                backgroundColor: saving || !hasChanges() ? 'var(--bg-subtle)' : '#10b981',
                color: saving || !hasChanges() ? 'var(--text-muted)' : 'white',
                transition: 'background-color 0.15s',
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Section: Basic Info */}
        <div style={sectionContainer}>
          <div style={{ ...sectionLabel, marginBottom: '12px' }}>Basic Info</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={fieldGroup}>
              <span style={sectionLabel}>Name</span>
              <input
                style={inputStyle}
                value={editedData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Cleaner Name"
              />
            </div>
            <div style={fieldGroup}>
              <span style={sectionLabel}>Phone</span>
              <input
                style={inputStyle}
                value={editedData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+51 999 999 999"
              />
            </div>
            <div style={fieldGroup}>
              <span style={sectionLabel}>Language</span>
              <select
                style={selectStyle}
                value={editedData.preferred_language || 'es'}
                onChange={(e) => handleChange('preferred_language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section: Property Assignments (read-only) */}
        <div style={sectionContainer}>
          <div style={{ ...sectionLabel, marginBottom: '10px' }}>Property Assignments</div>
          {assignments.length === 0 ? (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No properties assigned
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {assignments.map(a => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px', borderRadius: '8px',
                    backgroundColor: 'var(--bg-elevated)',
                  }}
                >
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '2px 8px', fontSize: '11px', fontWeight: '600',
                    borderRadius: '10px',
                    backgroundColor: 'var(--badge-green-bg)', color: 'var(--badge-green-text)',
                  }}>
                    P{a.priority}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                    {a.property?.name || `Property ${a.property_id}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section: Delete */}
        <div style={sectionContainer}>
          <div style={{ ...sectionLabel, marginBottom: '10px' }}>Danger Zone</div>
          <button
            onClick={handleDelete}
            style={{
              padding: '8px 14px', borderRadius: '6px', border: 'none', fontSize: '12px',
              fontWeight: '500', cursor: 'pointer',
              backgroundColor: 'var(--badge-red-bg)', color: 'var(--badge-red-text)',
            }}
          >
            Delete Cleaner
          </button>
        </div>

        {/* bottom spacer */}
        <div style={{ height: '40px' }} />
      </div>
    </div>
  );
};

export default CleanerManagePage;
